import { type ReactNode, useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { signOut, triggerHaptic } from '@repo/shared/client';
import { errorTracker } from '@repo/shared/common';
import { ShieldWarning, LogOut } from '~/components/icons';
import {
  ipAccessStatusQueryOptions,
  myOrganizationsQueryOptions,
  useSwitchOrganization,
} from '~/lib/queries/organization';
import { resolveLandingRoute } from '~/lib/landing';
import { Anchor, Button, Card, Reveal, Select, Stack, Text, Title } from '~/components/ui';
import styles from './blocked.module.css';

/**
 * "Your IP address isn't allowed" page. The `_protected` gate redirects here when
 * the active org has an IP allowlist the caller's IP isn't on. It lives under
 * `_authed` (session-only, NOT IP-gated) so it stays reachable while blocked.
 *
 * Shows the caller's own IP (never the allowlist), a switcher to any OTHER org
 * they belong to (the blocked/active org is disabled), a sign-out, and a support
 * mailto — the three ways out of a lockout.
 */
export const Route = createFileRoute('/_authed/blocked')({
  component: BlockedRoute,
});

function BlockedRoute(): ReactNode {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: ipStatus } = useQuery(ipAccessStatusQueryOptions());
  const { data: orgsData } = useQuery(myOrganizationsQueryOptions());
  const switchOrganization = useSwitchOrganization();
  const [switching, setSwitching] = useState(false);

  const clientIp = ipStatus?.clientIp ?? null;
  const supportEmail = ipStatus?.supportEmail ?? 'support@localhost';
  const organizations = orgsData?.organizations ?? [];
  const activeOrganizationId = orgsData?.activeOrganizationId ?? null;
  const activeOrg = organizations.find((o) => o.id === activeOrganizationId) ?? null;
  const hasOtherOrgs = organizations.length > 1;

  // Every org listed; the blocked (active) one is disabled so the user can only
  // pick a DIFFERENT org to escape into.
  const orgOptions = organizations.map((o) => ({
    value: o.id,
    label: o.id === activeOrganizationId ? t('blocked.currentOrgOption', { name: o.name }) : o.name,
    disabled: o.id === activeOrganizationId,
  }));

  async function handleSwitch(organizationId: string | null): Promise<void> {
    if (organizationId === null || organizationId === activeOrganizationId) return;
    triggerHaptic();
    setSwitching(true);
    try {
      await switchOrganization.mutateAsync({ body: { organizationId } });
      // Tenant changed — drop every cached read (like sign-out) so no blocked-org
      // data lingers, then land in the app; the gate re-evaluates the new org's
      // allowlist for this IP.
      queryClient.clear();
      void navigate({ to: await resolveLandingRoute(queryClient) });
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      errorTracker.captureException(error, { source: 'blockedPageSwitchOrg', organizationId });
      setSwitching(false);
    }
  }

  async function handleSignOut(): Promise<void> {
    triggerHaptic();
    await signOut();
    queryClient.clear();
    void navigate({ to: '/sign-in' });
  }

  return (
    <div className={styles.wrap ?? ''}>
      <Card className={styles.card ?? ''}>
        <Reveal>
          <Stack gap="lg" align="center">
            <span className={styles.icon ?? ''} aria-hidden>
              <ShieldWarning size={40} />
            </span>

            <Stack gap="xs" align="center">
              <Title order={1} ta="center">
                {t('blocked.title')}
              </Title>
              <Text c="dimmed" ta="center" size="sm">
                {activeOrg
                  ? t('blocked.bodyWithOrg', { name: activeOrg.name })
                  : t('blocked.body')}
              </Text>
            </Stack>

            <div className={styles.ipBlock ?? ''}>
              <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                {t('blocked.yourIpLabel')}
              </Text>
              <Text ff="monospace" fw={600} data-testid="blocked-client-ip">
                {clientIp ?? t('blocked.ipUnknown')}
              </Text>
            </div>

            {hasOtherOrgs ? (
              <Select
                label={t('blocked.switchOrgLabel')}
                data={orgOptions}
                value={null}
                placeholder={t('blocked.switchOrgPlaceholder')}
                onChange={(value) => void handleSwitch(value)}
                disabled={switching}
                comboboxProps={{ withinPortal: true }}
                w="100%"
                data-testid="blocked-org-switcher"
              />
            ) : null}

            <Button
              variant="subtle"
              color="gray"
              fullWidth
              leftSection={<LogOut size={16} aria-hidden />}
              onClick={() => void handleSignOut()}
              data-testid="blocked-sign-out"
            >
              {t('blocked.signOut')}
            </Button>

            <Text size="sm" c="dimmed" ta="center">
              {t('blocked.supportPrefix')}{' '}
              <Anchor href={`mailto:${supportEmail}`} data-testid="blocked-support-link">
                {supportEmail}
              </Anchor>
            </Text>
          </Stack>
        </Reveal>
      </Card>
    </div>
  );
}
